import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  EU_COMMISSION_NEWS_RSS_SOURCE,
  isEuLexRegulatoryNewsSubscription,
  regulatoryNewsCategoryKey,
  useRegulatoryNewsSubscriptions,
  useRegulatoryNewsTopics,
  useReplaceRegulatoryNewsSubscriptions,
  type RegulatoryNewsCategory,
} from '@/shared/hooks/api/regulatoryNews'
import { Button } from '@/components/ui/button'
import { RegulatoryNewsTopicPicker } from './RegulatoryNewsTopicPicker'

type RegulatoryNewsSettingsPanelProps = {
  onSaved?: () => void
}

function topicKeySetsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) {
    return false
  }
  for (const key of left) {
    if (!right.has(key)) {
      return false
    }
  }
  return true
}

export function RegulatoryNewsSettingsPanel({
  onSaved,
}: RegulatoryNewsSettingsPanelProps) {
  const { t } = useTranslation('regulatory-alerts')
  const { data: session } = useGetUserSession()
  const organizationId = session?.organizationId

  const { data: topics = [], isLoading: topicsLoading } =
    useRegulatoryNewsTopics()
  const { data: subscriptions = [], isLoading: subscriptionsLoading } =
    useRegulatoryNewsSubscriptions(organizationId)

  const replaceSubscriptions = useReplaceRegulatoryNewsSubscriptions()

  const euLexSubscriptions = useMemo(
    () => subscriptions.filter(isEuLexRegulatoryNewsSubscription),
    [subscriptions]
  )

  const topicKeys = useMemo(
    () => new Set(topics.map((topic) => regulatoryNewsCategoryKey(topic))),
    [topics]
  )

  const initialSelectedKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const subscription of euLexSubscriptions) {
      const key = regulatoryNewsCategoryKey({
        domain: subscription.domain,
        label: subscription.label,
      })
      if (topicKeys.has(key)) {
        keys.add(key)
      }
    }
    return keys
  }, [euLexSubscriptions, topicKeys])

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!subscriptionsLoading && !initialized) {
      setSelectedKeys(new Set(initialSelectedKeys))
      setInitialized(true)
    }
  }, [subscriptionsLoading, initialSelectedKeys, initialized])

  const handleToggle = (category: RegulatoryNewsCategory, checked: boolean) => {
    setSelectedKeys((previous) => {
      const next = new Set(previous)
      const key = regulatoryNewsCategoryKey(category)
      if (checked) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }

  const selectedCategories = useMemo(() => {
    return topics.filter((topic) =>
      selectedKeys.has(regulatoryNewsCategoryKey(topic))
    )
  }, [topics, selectedKeys])

  const hasUnsavedChanges = useMemo(() => {
    if (!initialized) {
      return false
    }
    return !topicKeySetsEqual(selectedKeys, initialSelectedKeys)
  }, [initialized, selectedKeys, initialSelectedKeys])

  const handleSave = async () => {
    if (!organizationId) {
      return
    }

    try {
      await replaceSubscriptions.mutateAsync({
        organizationId,
        source: EU_COMMISSION_NEWS_RSS_SOURCE,
        categories: selectedCategories,
      })
      toast.success(t('settings.savedSuccess'))
      onSaved?.()
    } catch {
      toast.error(t('settings.savedError'))
    }
  }

  const isLoading = topicsLoading || subscriptionsLoading
  const hasTopics = topics.length > 0
  const showNoTopics = !isLoading && !hasTopics

  return (
    <div className='flex flex-col gap-4 p-4'>
      {isLoading && (
        <div className='flex items-center gap-2 text-muted-foreground'>
          <Loader2 className='size-4 animate-spin' />
          {t('settings.loading')}
        </div>
      )}

      {showNoTopics && (
        <div className='rounded-lg border border-dashed p-4 text-center'>
          <h2 className='text-lg font-semibold'>
            {t('settings.noTopicsTitle')}
          </h2>
          <p className='mt-2 text-sm text-muted-foreground'>
            {t('settings.noTopicsDescription')}
          </p>
        </div>
      )}

      {!showNoTopics && (
        <>
          <RegulatoryNewsTopicPicker
            topics={topics}
            selectedKeys={selectedKeys}
            onToggle={handleToggle}
            disabled={replaceSubscriptions.isPending}
          />
          <Button
            onClick={() => {
              void handleSave()
            }}
            disabled={replaceSubscriptions.isPending || !hasUnsavedChanges}
          >
            {replaceSubscriptions.isPending ? (
              <Loader2 className='me-2 size-4 animate-spin' />
            ) : null}
            {t('settings.save')}
          </Button>
        </>
      )}
    </div>
  )
}
